import { BehaviorSubject } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Validators } from '@peca/commons';
import { DesignSystemModule } from '@peca/design-system';

import { IAsyncState } from '../../../../core/interfaces/async-state.interface';
import { ISearchResponse } from '../../../../core/interfaces/search-response.interface';
import { SearchService } from '../../services/search.service';
import { SearchAuthorizationComponent } from './search-authorization/search-authorization.component';
import { DialectService } from 'apps/dialect/src/services/dialect.service';
import { IDialectResponse } from 'apps/dialect/src/app/core/interfaces/dialect-response.interface';
import { SearchDialectDetailsComponent } from './search-dialect-details/search-dialect-details.component';
import { SalesChannelService } from '../../../../core/services/sales-channel-autorization.service';

@Component({
  selector: 'peca-search',
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss',
  imports: [DesignSystemModule, ReactiveFormsModule, CommonModule, SearchAuthorizationComponent, SearchDialectDetailsComponent],
})
export class SearchComponent {
  private readonly SEARCH_CACHE_SESSION_KEY = 'dialects/sales-channel/search-query';

  dialectService = inject(DialectService);

  searchService = inject(SearchService);

  salesChannelService = inject(SalesChannelService);

  search$ = new BehaviorSubject<IAsyncState<ISearchResponse>>({});

  dialect$ = new BehaviorSubject<IAsyncState<IDialectResponse>>({});

  form = new FormGroup({
    zipcode: new FormControl<string>('', [Validators.required, Validators.cep]),
    plate: new FormControl<string>('', [Validators.required]),
    dialect: new FormControl<string>('', [Validators.required]),
  });

  constructor() {
    const cache = sessionStorage.getItem(this.SEARCH_CACHE_SESSION_KEY);

    if (!cache) {
      return;
    }

    const { zipcode, plate, dialect } = JSON.parse(cache);

    this.form.setValue({ zipcode, plate, dialect });
  }

  get credentials$() {
    return this.salesChannelService.credentials$;
  }

  onClickSearch() {
    if (this.form.invalid) return;

    this.form.disable();
    this.search$.next({ loading: true });
    this.dialect$.next({ loading: true });

    const { zipcode, dialect, plate } = this.form.value as {
      zipcode: string;
      dialect: string;
      plate: string;
    };

    sessionStorage.setItem(this.SEARCH_CACHE_SESSION_KEY, JSON.stringify({ zipcode, plate, dialect }));
    this.dialectService.dialectDetails(dialect).subscribe({
      next: (data) => {
        this.dialect$.next({ data });
      },
      error: () => {
        this.dialect$.next({ failure: true });
      },
    });

    this.searchService.search(zipcode, dialect, plate).subscribe({
      next: (data) => {
        this.form.enable();
        this.search$.next({ data });
      },
      error: () => {
        this.form.enable();
        this.search$.next({ failure: true });
      },
    });
  }

  onClickClearFilter() {
    this.form.setValue({
      zipcode: '',
      plate: '',
      dialect: '',
    });
    this.form.clearValidators();
    this.form.updateValueAndValidity();
  }
}
